import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users, CalendarDays, Palette, Send, ClipboardCheck, BookOpen, ArrowRight,
  Clock, BellRing, Globe, Mail, Settings as SettingsIcon, BarChart3, QrCode,
  Sparkles, ShieldCheck, ListChecks,
} from 'lucide-react';
import { PageHeader } from '../components/layout/PageHeader';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from '../components/ui/accordion';

const QUICK_START = [
  {
    icon: Users,
    title: 'Add your guests',
    body: 'Import a CSV or Excel file of contacts, or add people one by one. Every guest gets a profile that tracks their RSVPs and email history across all your events.',
    cta: { label: 'Go to Contacts', to: '/app/contacts' },
  },
  {
    icon: CalendarDays,
    title: 'Create your event',
    body: 'Add the title, date, time, timezone and location. Your event starts as a Draft so you can perfect it — publish it when you\u2019re ready for guests to RSVP.',
    cta: { label: 'Create an event', to: '/app/events/new' },
  },
  {
    icon: Palette,
    title: 'Design & preview the invite',
    body: 'Pick one of six flyer layouts and colors. Use “Preview email” to see the exact invitation your guests will receive in their inbox — QR codes and all.',
    cta: { label: 'View events', to: '/app/events' },
  },
  {
    icon: Send,
    title: 'Send or schedule invites',
    body: 'Select your guests and send right away, or schedule the invite for later. Then add automatic reminders — 1 week, 24 hours, 1 hour before, or any custom time.',
    cta: null,
  },
  {
    icon: ClipboardCheck,
    title: 'Track RSVPs & check guests in',
    body: 'Watch RSVPs roll in, see who\u2019s bringing what, mark guests as attended at the door, and export everything to CSV whenever you need it.',
    cta: null,
  },
];

const TOPICS = [
  {
    icon: Users,
    title: 'Contacts & lists',
    points: [
      'Import contacts from CSV, Excel, or a Google Sheets link — names, emails, phone, address, company and dietary notes are all captured.',
      'Organize people into reusable lists (e.g. “Family”, “Work”) so you can invite a whole group in one click.',
      'Open any contact to see their full history: every event they were invited to, how they RSVP\u2019d, and their email engagement.',
      'Duplicate and inactive contacts are flagged so your list stays clean.',
    ],
  },
  {
    icon: CalendarDays,
    title: 'Events & their lifecycle',
    points: [
      'Every event moves through three stages: Draft (private, still editing), Published (guests can open the link and RSVP), and Past (after the date).',
      'Guests can only RSVP once an event is Published — draft links won\u2019t open.',
      'Set a timezone per event; all times display on a friendly 12-hour clock.',
    ],
  },
  {
    icon: Palette,
    title: 'The invitation flyer',
    points: [
      'Choose from six layouts — Classic, Bold Poster, Photo-forward, Minimal, Festive and Editorial — each with matching email and printable versions.',
      'Add a cover image, set your theme and accent colors, and include an embedded map of the venue.',
      'Print flyers come with a QR code that opens the event and lets people RSVP.',
    ],
  },
  {
    icon: Send,
    title: 'Sending invites',
    points: [
      'In the “Send invites” tab, search or load a saved list, select recipients, add an optional personal note, and send.',
      'Toggles let you skip already-invited people or only message those who haven\u2019t responded.',
      'Each invitation carries a private, per-guest link — guests who use it can see the address and RSVP, keeping your event invitation-only.',
      'Use “Copy RSVP link” to share a single shareable invite link anywhere (texts, social, etc.).',
    ],
  },
  {
    icon: Clock,
    title: 'Scheduling & automatic reminders',
    points: [
      'Turn on “Schedule for later” when sending to have the invitation go out automatically at a future date and time.',
      'In the “Schedule” tab, add reminder emails: 1 week, 24 hours, or 1 hour before — or a custom time, or a specific date.',
      'Pick who each reminder goes to: everyone invited, only non-responders, or only guests who RSVP\u2019d “Going”.',
      'Reminders automatically skip anyone who unsubscribed or RSVP\u2019d “Not going”. Preview any reminder before it sends.',
    ],
  },
  {
    icon: ClipboardCheck,
    title: 'RSVPs, guests & attendance',
    points: [
      'Build a custom RSVP form — ask what guests are bringing, how many are coming in total, dietary needs, and more. You control which fields show.',
      'The Guests tab shows every response in real time, including a potluck/“who\u2019s bringing what” view.',
      'Check guests in at the door with one tap, then export guests, potluck items, or attendance to CSV.',
    ],
  },
  {
    icon: Mail,
    title: 'Subscribers & unsubscribes',
    points: [
      'Guests can opt out of future emails via the unsubscribe link in any message — their data is kept, they\u2019re just never emailed again.',
      'Manage your email list and review opt-outs on the Subscribers page.',
    ],
  },
  {
    icon: BarChart3,
    title: 'Email analytics',
    points: [
      'See delivered, opened, clicked and bounced counts for every send.',
      'Engagement is tracked per guest, so you know exactly who opened your invitation.',
    ],
  },
  {
    icon: Globe,
    title: 'Your public landing page',
    points: [
      'You get a public, dark-themed landing page that showcases featured events and photo carousels.',
      'Edit the hero text, about section, and featured events from the “Landing Page” manager.',
      'Public pages show the venue name but keep the full address private until someone is invited.',
    ],
  },
  {
    icon: SettingsIcon,
    title: 'Settings (reply-to email)',
    points: [
      'Set a Reply-to email so when a guest hits “Reply” on an invitation, their message lands in the inbox you choose.',
      'Update your display name, which appears as the sender on invitations.',
    ],
  },
];

const Guide = () => {
  const navigate = useNavigate();

  return (
    <div data-testid="guide-page">
      <PageHeader
        title="Host guide"
        subtitle="Everything you need to plan, send, and track beautiful event invitations."
      />

      {/* Intro callout */}
      <Card className="mb-8 border-primary/20 bg-primary/5">
        <CardContent className="flex items-start gap-4 p-5">
          <div className="h-11 w-11 rounded-2xl bg-primary/10 flex items-center justify-center shrink-0">
            <Sparkles className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h2 className="font-display text-lg font-semibold text-foreground">New here? Start with the 5 steps below.</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Follow the Quick Start to send your first invitation in minutes. Scroll down for detailed how-tos on every feature.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Quick start */}
      <div className="mb-10">
        <div className="flex items-center gap-2 mb-4">
          <ListChecks className="h-[18px] w-[18px] text-primary" />
          <h2 className="font-display text-xl font-semibold text-foreground">Quick start</h2>
          <Badge variant="secondary" className="ml-1">5 steps</Badge>
        </div>

        <div className="space-y-3" data-testid="quick-start-list">
          {QUICK_START.map((step, i) => {
            const Icon = step.icon;
            return (
              <Card key={step.title} className="overflow-hidden transition-colors hover:border-primary/30" data-testid="quick-start-step">
                <CardContent className="flex flex-col sm:flex-row sm:items-center gap-4 p-5">
                  <div className="flex items-center gap-4 min-w-0 flex-1">
                    <div className="relative shrink-0">
                      <div className="h-11 w-11 rounded-2xl bg-secondary flex items-center justify-center">
                        <Icon className="h-5 w-5 text-foreground" />
                      </div>
                      <span className="absolute -top-2 -left-2 h-6 w-6 rounded-full bg-primary text-primary-foreground text-xs font-semibold flex items-center justify-center">
                        {i + 1}
                      </span>
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-medium text-foreground">{step.title}</h3>
                      <p className="text-sm text-muted-foreground mt-0.5">{step.body}</p>
                    </div>
                  </div>
                  {step.cta && (
                    <Button
                      variant="outline"
                      className="gap-2 shrink-0 self-start sm:self-center"
                      onClick={() => navigate(step.cta.to)}
                      data-testid={`quick-start-cta-${i}`}
                    >
                      {step.cta.label} <ArrowRight className="h-4 w-4" />
                    </Button>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Detailed topics */}
      <div className="mb-10">
        <div className="flex items-center gap-2 mb-4">
          <BookOpen className="h-[18px] w-[18px] text-primary" />
          <h2 className="font-display text-xl font-semibold text-foreground">Feature guides</h2>
        </div>

        <Accordion type="single" collapsible className="space-y-3" data-testid="guide-topics">
          {TOPICS.map((topic, i) => {
            const Icon = topic.icon;
            return (
              <AccordionItem
                key={topic.title}
                value={`topic-${i}`}
                className="rounded-2xl border border-border bg-card px-4 data-[state=open]:border-primary/30"
                data-testid="guide-topic"
              >
                <AccordionTrigger className="hover:no-underline py-4">
                  <span className="flex items-center gap-3 text-left">
                    <span className="h-9 w-9 rounded-xl bg-secondary flex items-center justify-center shrink-0">
                      <Icon className="h-[18px] w-[18px] text-primary" />
                    </span>
                    <span className="font-medium text-foreground">{topic.title}</span>
                  </span>
                </AccordionTrigger>
                <AccordionContent className="pb-4">
                  <ul className="space-y-2.5 pl-1">
                    {topic.points.map((p, j) => (
                      <li key={j} className="flex items-start gap-2.5 text-sm text-muted-foreground">
                        <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-primary/60 shrink-0" />
                        <span>{p}</span>
                      </li>
                    ))}
                  </ul>
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      </div>

      {/* Footer tips */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-5">
            <ShieldCheck className="h-5 w-5 text-primary mb-2" />
            <h3 className="font-medium text-foreground text-sm">Invitation-only by default</h3>
            <p className="text-xs text-muted-foreground mt-1">Addresses stay private and RSVPs are gated — only people with your link can respond.</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <QrCode className="h-5 w-5 text-primary mb-2" />
            <h3 className="font-medium text-foreground text-sm">QR codes everywhere</h3>
            <p className="text-xs text-muted-foreground mt-1">Printed flyers and emails include scannable codes that open the event and RSVP form.</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <BellRing className="h-5 w-5 text-primary mb-2" />
            <h3 className="font-medium text-foreground text-sm">Set it and forget it</h3>
            <p className="text-xs text-muted-foreground mt-1">Schedule the invite and reminders once — Thrive sends them automatically at the right time.</p>
          </CardContent>
        </Card>
      </div>

      <div className="mt-8 flex flex-wrap gap-3">
        <Button onClick={() => navigate('/app/events/new')} className="gap-2" data-testid="guide-create-event-button">
          <CalendarDays className="h-4 w-4" /> Create your first event
        </Button>
        <Button variant="outline" onClick={() => navigate('/app/contacts')} className="gap-2" data-testid="guide-import-contacts-button">
          <Users className="h-4 w-4" /> Import contacts
        </Button>
      </div>
    </div>
  );
};

export default Guide;
